import { useState, useRef, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2 } from 'lucide-react';

interface SessionInvalidatedOverlayProps {
  type: 'logout' | 'account-switch';
}

const COPY = {
  logout: {
    title: "You've been signed out",
    description: 'Your session ended in another tab. Sign in to continue.',
  },
  'account-switch': {
    title: 'Another account signed in',
    description: 'A different account was used in another tab. Sign in to continue.',
  },
} as const;

export function SessionInvalidatedOverlay({ type }: SessionInvalidatedOverlayProps) {
  const [isNavigating, setIsNavigating] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const copy = COPY[type];

  // Capture the redirect path once on mount (before any state changes affect the URL)
  const [redirectPath] = useState(
    () => window.location.pathname + window.location.search
  );

  // Focus the button on mount
  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  const handleSignIn = () => {
    setIsNavigating(true);
    window.location.href = '/login?redirect=' + encodeURIComponent(redirectPath);
  };

  // Focus trap: keep focus on the button (single focusable element)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab') {
      e.preventDefault();
      buttonRef.current?.focus();
    }
    // Do NOT handle Escape — overlay is non-dismissible
  };

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-overlay-title"
      aria-describedby="session-overlay-desc"
      onKeyDown={handleKeyDown}
      style={{ animation: 'sessionOverlayIn 150ms ease-out' }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Card */}
      <Card className="relative w-full max-w-[380px] shadow-lg">
        <CardHeader className="text-center space-y-2 pb-2">
          <CardTitle id="session-overlay-title" className="text-xl">
            {copy.title}
          </CardTitle>
          <CardDescription id="session-overlay-desc">
            {copy.description}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            ref={buttonRef}
            className="w-full"
            onClick={handleSignIn}
            disabled={isNavigating}
            autoFocus
          >
            {isNavigating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Redirecting...
              </>
            ) : (
              'Sign In Again'
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Animation keyframes + reduced motion support */}
      <style>{`
        @keyframes sessionOverlayIn {
          from { opacity: 0; transform: scale(0.97); }
          to { opacity: 1; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          @keyframes sessionOverlayIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        }
      `}</style>

      {/* Screen reader announcement */}
      <div className="sr-only" aria-live="assertive">
        {copy.title}. {copy.description}
      </div>
    </div>
  );
}
